import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { toast } from "sonner";
import { LOGO_URL } from "@/data/site";
import { TicketPercent, Plus, Power, CalendarDays, FileImage, BellRing, Upload, CreditCard, Users, CheckCircle2 } from "lucide-react";

const inputCls =
  "bg-[#0D0E13] border border-white/10 rounded-2xl px-5 py-3.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-orange-500/70 transition-colors duration-300";

const BOOKING_STATUSES = ["In elaborazione", "Confermata", "Completata", "Annullata"];

const TABS = [
  { id: "promo", label: "Codici Promo", icon: TicketPercent },
  { id: "prenotazioni", label: "Prenotazioni", icon: CalendarDays },
  { id: "acquisti", label: "Acquisti", icon: CreditCard },
  { id: "clienti", label: "Clienti", icon: Users },
  { id: "report", label: "Report Clienti", icon: FileImage },
  { id: "notifiche", label: "Notifiche", icon: BellRing },
];

function PromoTab() {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", discount_pct: 10 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/promo-codes").then((r) => setCodes(r.data.promo_codes)).catch(() => toast.error("Errore caricamento codici"));
  }, []);
  useEffect(load, [load]);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/promo-codes", form);
      toast.success(`Codice ${form.code.toUpperCase()} creato`);
      setForm({ code: "", discount_pct: 10 });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggle = async (id) => {
    try {
      await api.post(`/admin/promo-codes/${id}/toggle`);
      load();
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  return (
    <>
      <form onSubmit={create} className="bg-[#13141B] border border-white/10 rounded-3xl p-7 flex flex-col sm:flex-row gap-4 items-stretch sm:items-end" data-testid="admin-promo-create-form">
        <div className="flex-1">
          <label className="font-mono-adj text-[9px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Nuovo codice</label>
          <input required minLength={3} maxLength={24} placeholder="ES. AMICO15" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={`${inputCls} w-full uppercase font-mono-adj tracking-widest`} data-testid="admin-promo-code-input" />
        </div>
        <div className="w-full sm:w-36">
          <label className="font-mono-adj text-[9px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Sconto %</label>
          <input required type="number" min={1} max={50} value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: Number(e.target.value) })} className={`${inputCls} w-full`} data-testid="admin-promo-pct-input" />
        </div>
        <button type="submit" disabled={loading} data-testid="admin-promo-create-button" className="fire-bg text-white font-mono-adj text-[11px] uppercase tracking-[0.2em] font-semibold rounded-full px-7 py-3.5 hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
          <Plus size={15} /> Crea Codice
        </button>
      </form>

      <div className="flex flex-col gap-4 mt-8" data-testid="admin-promo-list">
        {codes.map((c) => (
          <div key={c.id} className={`bg-[#13141B] border rounded-2xl px-6 py-5 flex items-center justify-between gap-4 transition-colors ${c.active ? "border-orange-500/40" : "border-white/10 opacity-60"}`} data-testid={`admin-promo-item-${c.code}`}>
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.active ? "fire-bg" : "bg-white/5 border border-white/10"}`}>
                <TicketPercent size={17} className={c.active ? "text-white" : "text-slate-500"} />
              </div>
              <div>
                <p className="font-mono-adj font-semibold text-white tracking-[0.2em]">{c.code}</p>
                <p className="text-xs text-slate-500 mt-0.5">Sconto {c.discount_pct}% · {c.active ? "Attivo" : "Disattivato"}</p>
              </div>
            </div>
            <button onClick={() => toggle(c.id)} data-testid={`admin-promo-toggle-${c.code}`} className={`flex items-center gap-2 font-mono-adj text-[10px] uppercase tracking-[0.15em] rounded-full px-4 py-2.5 border transition-colors ${c.active ? "border-red-500/40 text-red-400 hover:bg-red-500/10" : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"}`}>
              <Power size={13} /> {c.active ? "Disattiva" : "Attiva"}
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/bookings").then((r) => setBookings(r.data.bookings)).catch(() => toast.error("Errore caricamento"));
  }, []);
  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/bookings/${id}/status`, { status });
      toast.success("Stato aggiornato");
      load();
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const confirmAvailability = async (b) => {
    try {
      await api.post(`/admin/bookings/${b.id}/status`, { status: "Confermata" });
      await api.post("/admin/notifications", {
        target: b.user_id,
        kind: "info",
        title: "Disponibilità confermata",
        message: `La tua prenotazione ${b.kw} kW (${b.plan_label}) per il ${b.date} a ${b.city} è confermata. Ti aspettiamo!`,
      });
      toast.success("Disponibilità confermata e cliente avvisato");
      load();
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="admin-bookings-list">
      {bookings.length === 0 && <p className="text-slate-500 text-sm">Nessuna prenotazione ricevuta.</p>}
      {bookings.map((b) => (
        <div key={b.id} className="bg-[#13141B] border border-white/10 rounded-2xl p-6" data-testid={`admin-booking-${b.id.slice(0, 8)}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-heading font-bold text-white">{b.name} · {b.kw} kW — €{b.price}</p>
              <p className="text-xs text-slate-500 mt-1">{b.plan_label} · {b.date} · {b.address}, {b.city}</p>
              <p className="text-xs text-slate-500">{b.email} · {b.phone}</p>
              <div className="flex gap-2 mt-2">
                <span className={`font-mono-adj text-[9px] uppercase tracking-[0.15em] border rounded-full px-2.5 py-1 ${b.payment_status === "pagato" ? "text-emerald-400 border-emerald-500/30" : "text-slate-400 border-white/15"}`}>
                  {b.payment_status === "pagato" ? "Pagato online" : "Da saldare"}
                </span>
                {b.promo_code && <span className="font-mono-adj text-[9px] uppercase tracking-[0.15em] border border-orange-500/30 text-orange-400 rounded-full px-2.5 py-1">{b.promo_code}</span>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {b.status === "In elaborazione" && (
                <button
                  onClick={() => confirmAvailability(b)}
                  data-testid={`admin-booking-confirm-${b.id.slice(0, 8)}`}
                  className="flex items-center gap-2 fire-bg text-white font-mono-adj text-[10px] uppercase tracking-[0.15em] font-semibold rounded-full px-4 py-2.5 hover:opacity-90 transition-opacity"
                >
                  <CheckCircle2 size={13} /> Conferma Disponibilità
                </button>
              )}
              <select
                value={b.status}
                onChange={(e) => setStatus(b.id, e.target.value)}
                data-testid={`admin-booking-status-${b.id.slice(0, 8)}`}
                className={`${inputCls} w-auto text-xs`}
              >
                {BOOKING_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportsTab() {
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ booking_id: "", title: "", note: "" });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/bookings").then((r) => setBookings(r.data.bookings)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const booking = bookings.find((b) => b.id === form.booking_id);
    if (!booking) {
      toast.error("Seleziona una prenotazione");
      return;
    }
    setSending(true);
    try {
      const media = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("user_id", booking.user_id);
        fd.append("file", f);
        const res = await api.post("/admin/uploads", fd);
        media.push({
          path: res.data.path,
          type: res.data.content_type.startsWith("video") ? "video" : "image",
          name: res.data.name,
        });
      }
      await api.post("/admin/reports", { ...form, media });
      toast.success("Report pubblicato nell'area del cliente");
      setForm({ booking_id: "", title: "", note: "" });
      setFiles([]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Pubblicazione report fallita"));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#13141B] border border-white/10 rounded-3xl p-7 flex flex-col gap-4" data-testid="admin-report-form">
      <p className="text-sm text-slate-400">Pubblica un report con foto e video della pulizia nell'area personale del cliente.</p>
      <div>
        <label className="font-mono-adj text-[9px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Prenotazione del cliente</label>
        <select required value={form.booking_id} onChange={(e) => setForm({ ...form, booking_id: e.target.value })} className={`${inputCls} w-full`} data-testid="admin-report-booking-select">
          <option value="">Seleziona prenotazione...</option>
          {bookings.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name} — {b.kw} kW — {b.date} — {b.city}
            </option>
          ))}
        </select>
      </div>
      <input required placeholder="Titolo report (es. Report pulizia impianto 6 kW)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="admin-report-title-input" />
      <textarea rows={3} placeholder="Note per il cliente: esito, resa stimata, consigli..." value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={`${inputCls} resize-none`} data-testid="admin-report-note-input" />
      <div>
        <label className="font-mono-adj text-[9px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Foto e video dell'intervento</label>
        <label className="flex items-center justify-center gap-3 border border-dashed border-white/20 rounded-2xl px-6 py-8 cursor-pointer hover:border-orange-500/50 transition-colors" data-testid="admin-report-file-drop">
          <Upload size={18} className="text-orange-500" />
          <span className="text-sm text-slate-400">{files.length > 0 ? `${files.length} file selezionati` : "Clicca per caricare foto/video"}</span>
          <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} data-testid="admin-report-file-input" />
        </label>
      </div>
      <button type="submit" disabled={sending} data-testid="admin-report-submit-button" className="fire-bg text-white font-mono-adj text-[11px] uppercase tracking-[0.2em] font-semibold rounded-full px-7 py-4 hover:opacity-90 active:scale-95 transition-all disabled:opacity-50">
        {sending ? "Caricamento in corso..." : "Pubblica Report"}
      </button>
    </form>
  );
}

function NotificationsTab() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ target: "all", kind: "info", title: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/admin/notifications", form);
      toast.success("Notifica inviata");
      setForm({ target: "all", kind: "info", title: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#13141B] border border-white/10 rounded-3xl p-7 flex flex-col gap-4" data-testid="admin-notification-form">
      <p className="text-sm text-slate-400">Invia una notifica o una promo: apparirà nell'area clienti dei destinatari.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="font-mono-adj text-[9px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Destinatario</label>
          <select value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className={`${inputCls} w-full`} data-testid="admin-notification-target-select">
            <option value="all">Tutti i clienti</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="font-mono-adj text-[9px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Tipo</label>
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={`${inputCls} w-full`} data-testid="admin-notification-kind-select">
            <option value="info">Info</option>
            <option value="promo">Promo</option>
          </select>
        </div>
      </div>
      <input required placeholder="Titolo notifica" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="admin-notification-title-input" />
      <textarea required rows={3} placeholder="Messaggio: nuova promo, avviso manutenzione, aggiornamento app..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} data-testid="admin-notification-message-input" />
      <button type="submit" disabled={sending} data-testid="admin-notification-submit-button" className="fire-bg text-white font-mono-adj text-[11px] uppercase tracking-[0.2em] font-semibold rounded-full px-7 py-4 hover:opacity-90 active:scale-95 transition-all disabled:opacity-50">
        {sending ? "Invio..." : "Invia Notifica"}
      </button>
    </form>
  );
}

function PaymentsTab() {
  const [payments, setPayments] = useState([]);
  useEffect(() => {
    api.get("/admin/payments").then((r) => setPayments(r.data.payments)).catch(() => toast.error("Errore caricamento acquisti"));
  }, []);

  const PAY_STYLE = {
    paid: "text-emerald-400 border-emerald-500/30",
    pending: "text-amber-400 border-amber-500/30",
    failed: "text-red-400 border-red-500/30",
    expired: "text-slate-400 border-white/15",
    refunded: "text-slate-300 border-white/20",
  };
  const PAY_LABEL = { paid: "Pagato", pending: "In attesa", failed: "Fallito", expired: "Scaduto", refunded: "Rimborsato" };

  const total = payments.filter((p) => p.payment_status === "paid").reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className="flex flex-col gap-4" data-testid="admin-payments-list">
      <div className="bg-[#13141B] border border-orange-500/30 rounded-2xl px-6 py-5 flex items-center justify-between" data-testid="admin-payments-total">
        <p className="font-mono-adj text-[10px] uppercase tracking-[0.25em] text-slate-500">Totale incassato online</p>
        <p className="font-display font-bold text-3xl fire-text">€{total.toLocaleString("it-IT")}</p>
      </div>
      {payments.length === 0 && <p className="text-slate-500 text-sm">Nessun pagamento online ancora.</p>}
      {payments.map((p) => (
        <div key={p.session_id} className="bg-[#13141B] border border-white/10 rounded-2xl p-6" data-testid={`admin-payment-${p.session_id.slice(-8)}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-heading font-bold text-white">
                €{p.amount} <span className="text-slate-500 font-normal text-sm">· {p.booking ? `${p.booking.name} — ${p.booking.kw} kW ${p.booking.plan_label}` : "prenotazione rimossa"}</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {p.booking?.email} · {new Date(p.created_at).toLocaleString("it-IT")}
              </p>
            </div>
            <span className={`font-mono-adj text-[9px] uppercase tracking-[0.15em] border rounded-full px-2.5 py-1 ${PAY_STYLE[p.payment_status] || "text-slate-400 border-white/15"}`} data-testid={`admin-payment-status-${p.session_id.slice(-8)}`}>
              {PAY_LABEL[p.payment_status] || p.payment_status}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomersTab() {
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => toast.error("Errore caricamento clienti"));
    api.get("/admin/bookings").then((r) => setBookings(r.data.bookings)).catch(() => {});
  }, []);

  const byUser = (id) => bookings.filter((b) => b.user_id === id);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/customers/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clienti_airdronejet.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Elenco clienti scaricato");
    } catch {
      toast.error("Export non riuscito");
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="admin-customers-list">
      <div className="flex justify-end">
        <button
          onClick={exportCsv}
          data-testid="admin-customers-export-button"
          className="flex items-center gap-2 border border-orange-500/50 text-orange-400 font-mono-adj text-[10px] uppercase tracking-[0.15em] rounded-full px-5 py-3 hover:bg-orange-500/10 transition-colors"
        >
          <Upload size={13} className="rotate-180" /> Scarica CSV
        </button>
      </div>
      {users.length === 0 && <p className="text-slate-500 text-sm">Nessun cliente registrato.</p>}
      {users.map((u) => {
        const ub = byUser(u.id);
        const paid = ub.filter((b) => b.payment_status === "pagato").reduce((s, b) => s + b.price, 0);
        const phone = ub.find((b) => b.phone)?.phone;
        return (
          <div key={u.id} className="bg-[#13141B] border border-white/10 rounded-2xl p-6" data-testid={`admin-customer-${u.id.slice(0, 8)}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-heading font-bold text-white">{u.name}</p>
                <p className="text-xs text-slate-500 mt-1">{u.email}{phone ? ` · ${phone}` : ""}</p>
                <p className="text-xs text-slate-600 mt-1">Cliente dal {new Date(u.created_at).toLocaleDateString("it-IT")}</p>
              </div>
              <div className="flex gap-6 text-right">
                <div>
                  <p className="font-display font-bold text-2xl text-white">{ub.length}</p>
                  <p className="font-mono-adj text-[9px] uppercase tracking-[0.15em] text-slate-500">Prenotazioni</p>
                </div>
                <div>
                  <p className="font-display font-bold text-2xl fire-text">€{paid}</p>
                  <p className="font-mono-adj text-[9px] uppercase tracking-[0.15em] text-slate-500">Pagato online</p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Admin() {
  const { user, checking, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [tab, setTab] = useState("promo");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  let body;
  if (checking) {
    body = <p className="text-slate-500 text-sm" data-testid="admin-loading">Caricamento...</p>;
  } else if (!user) {
    body = (
      <div className="text-center py-20" data-testid="admin-login-prompt">
        <TicketPercent size={40} className="text-orange-500 mx-auto mb-5" />
        <h2 className="font-heading text-2xl font-bold text-white">Area Amministratore</h2>
        <p className="text-slate-400 text-sm mt-3 mb-7">Accedi con un account amministratore per continuare.</p>
        <button onClick={() => setAuthOpen(true)} data-testid="admin-login-button" className="fire-bg text-white font-mono-adj text-xs uppercase tracking-[0.2em] font-semibold rounded-full px-8 py-4 hover:opacity-90 transition-opacity">
          Accedi
        </button>
      </div>
    );
  } else if (user.role !== "admin") {
    body = (
      <div className="text-center py-20" data-testid="admin-access-denied">
        <h2 className="font-heading text-2xl font-bold text-white">Accesso riservato</h2>
        <p className="text-slate-400 text-sm mt-3 mb-7">Questa pagina è disponibile solo per gli amministratori.</p>
        <button
          onClick={() => {
            logout();
            setAuthOpen(true);
          }}
          data-testid="admin-switch-account-button"
          className="border border-orange-500/50 text-orange-400 font-mono-adj text-[11px] uppercase tracking-[0.2em] rounded-full px-8 py-4 hover:bg-orange-500/10 transition-colors"
        >
          Accedi con altro account
        </button>
      </div>
    );
  } else {
    body = (
      <>
        <div className="flex flex-wrap gap-2 mb-8 bg-[#0D0E13] border border-white/10 rounded-full p-1.5 w-fit" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-mono-adj text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${tab === t.id ? "fire-bg text-white font-semibold" : "text-slate-400 hover:text-white"}`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "promo" && <PromoTab />}
        {tab === "prenotazioni" && <BookingsTab />}
        {tab === "acquisti" && <PaymentsTab />}
        {tab === "clienti" && <CustomersTab />}
        {tab === "report" && <ReportsTab />}
        {tab === "notifiche" && <NotificationsTab />}
      </>
    );
  }

  return (
    <div className="bg-[#050507] min-h-screen" data-testid="admin-page">
      <header className="glass border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo-link">
            <img src={LOGO_URL} alt="AirDroneJet" className="h-9 w-auto" />
            <span className="font-heading font-extrabold tracking-tight">
              AIR<span className="fire-text">DRONE</span>JET
            </span>
          </Link>
          <Link to="/" data-testid="admin-back-home-link" className="font-mono-adj text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-orange-400 transition-colors">
            ← Torna alla Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="font-mono-adj text-xs uppercase tracking-[0.3em] text-orange-500 font-semibold">Area Amministratore</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-3 mb-8">
            Gestione <span className="fire-text">AirDroneJet</span>
          </h1>
        </motion.div>
        {body}
      </main>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}
